import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import {
  useInboundPackageByIdQuery,
  useIdentifyItemMutation,
  useUpdateItemStepMutation,
  useSearchItemMutation,
} from 'api/warehouse';
import {
  useAttachmentBulkCreateMutation,
  useAttachmentFileBulkCreateMutation,
  useAttachmentDeleteMutation,
  useAttachmentFileDeleteMutation,
} from 'api';
import { WAREHOUSE_ROUTES } from 'constants/routes';

interface IdentifiedItem {
  id: string;
  sku: string;
  name?: string;
  matched: boolean;
}

interface UploadError {
  title: string;
  message: string;
  type: 'parse_error' | 'not_in_order';
  sku?: string;
}

interface LocalImage {
  file: File;
  url: string;
  isIdentifier: boolean;
  attachmentId?: string; // If image is already uploaded to backend
  fileName?: string; // Original file name for deletion
}

export const useCaptureItem = () => {
  const { message } = App.useApp();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasStarted, setHasStarted] = useState(false);
  const [isIdentifying, setIsIdentifying] = useState(false);
  const [identifiedItem, setIdentifiedItem] = useState<IdentifiedItem | null>(null);
  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [identificationImage, setIdentificationImage] = useState<LocalImage | null>(null);
  const [additionalImages, setAdditionalImages] = useState<LocalImage[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasAutoOpenedRef = useRef<boolean>(false);
  const hasLoadedFromStateRef = useRef<boolean>(false);

  const itemInfo = location.state as { itemId: string; sku: string } | undefined;

  const {
    data: order,
    isLoading,
    error,
  } = useInboundPackageByIdQuery(id || '', {
    skip: !id,
  });

  const [identifyItem] = useIdentifyItemMutation();
  const [searchItem] = useSearchItemMutation();
  const [createBulkAttachments] = useAttachmentBulkCreateMutation();
  const [uploadBulkAttachmentFiles] = useAttachmentFileBulkCreateMutation();
  const [deleteAttachment] = useAttachmentDeleteMutation();
  const [deleteAttachmentFile] = useAttachmentFileDeleteMutation();
  const [updateItemStep] = useUpdateItemStepMutation();

  useEffect(() => {
    if (error) {
      message.error('Failed to load order details');
      navigate(WAREHOUSE_ROUTES.inboundPackages);
    }
  }, [error, navigate, message]);

  // Load already identified item from order details if coming back from next steps
  useEffect(() => {
    if (
      itemInfo?.itemId &&
      order?.itemsResponseList &&
      !hasLoadedFromStateRef.current &&
      !identifiedItem &&
      !identificationImage
    ) {
      const orderItem = order.itemsResponseList.find((item: any) => item.id === itemInfo.itemId);

      if (orderItem && orderItem.attachments && orderItem.attachments.length > 0) {
        hasLoadedFromStateRef.current = true;
        hasAutoOpenedRef.current = true; // Prevent auto-opening camera
        setHasStarted(true);

        // Load identified item
        setIdentifiedItem({
          id: orderItem.id,
          sku: orderItem.sku,
          name: orderItem.description,
          matched: true,
        });

        // Load attachments
        const identifierAttachment = orderItem.attachments.find(
          (att: any) => att.attachmentType === 'WAREHOUSE_IDENTIFIER'
        );
        const otherAttachments = orderItem.attachments.filter(
          (att: any) => att.attachmentType !== 'WAREHOUSE_IDENTIFIER'
        );

        // Set identifier image
        if (identifierAttachment?.files?.ATTACHMENTS) {
          setIdentificationImage({
            file: null as any,
            url: identifierAttachment.files.ATTACHMENTS,
            isIdentifier: true,
            attachmentId: identifierAttachment.id,
            fileName: identifierAttachment.fileName,
          });
        }

        // Set additional images
        const additionalImgs = otherAttachments.map((att: any) => ({
          file: null as any,
          url: att.files.ATTACHMENTS,
          isIdentifier: false,
          attachmentId: att.id,
          fileName: att.fileName,
        }));
        setAdditionalImages(additionalImgs);
      }
    }
  }, [itemInfo, order, identifiedItem, identificationImage]);

  // Auto-open camera on mount (only once) - only if not loading from state
  useEffect(() => {
    if (
      !hasAutoOpenedRef.current &&
      !isIdentifying &&
      !identifiedItem &&
      !uploadError &&
      !identificationImage &&
      !itemInfo?.itemId
    ) {
      const timer = setTimeout(() => {
        fileInputRef.current?.click();
        hasAutoOpenedRef.current = true;
        setHasStarted(true); // Mark as started immediately
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isIdentifying, identifiedItem, uploadError, identificationImage, itemInfo]);

  const handleIdentification = useCallback(
    async (file: File) => {
      try {
        setIsIdentifying(true);
        setUploadError(null);
        setIdentifiedItem(null);

        const imageUrl = URL.createObjectURL(file);
        setIdentificationImage({ file, url: imageUrl, isIdentifier: true });

        const formData = new FormData();
        formData.append('image', file);
        formData.append('orderId', id || '');

        const response: any = await identifyItem(formData);

        if (response?.data?.success) {
          const item = response.data.data;

          // Check identification type
          if (item.identificationType === 'NOT_IDENTIFIED') {
            setUploadError({
              title: "Couldn't parse the image",
              message: 'Please retake image to proceed.',
              type: 'parse_error',
            });
            message.error({ content: 'Failed to identify item', key: 'identify' });
            return;
          }

          // Item is identified, now check if it's in the order by SKU
          const orderItem = order?.itemsResponseList?.find((orderItem: any) => orderItem.sku === item.sku);

          if (!orderItem) {
            setUploadError({
              title: 'Identified item is not in the order',
              message: 'Please retake image to proceed.',
              type: 'not_in_order',
              sku: item.sku,
            });
            message.error({ content: 'Item not in order', key: 'identify' });
          } else {
            // Use the order item's ID (not the identification service's itemId)
            setIdentifiedItem({
              id: orderItem.id,
              sku: item.sku,
              name: item.itemDescription,
              matched: true,
            });

            // Load existing attachments from API response
            if (item.attachments && Array.isArray(item.attachments)) {
              const existingImages: LocalImage[] = [];
              let identifierSet = false;

              item.attachments.forEach((attachment: any) => {
                // Skip attachments without files
                if (!attachment.files || !attachment.files.ATTACHMENTS) {
                  return;
                }

                const isIdentifier = attachment.attachmentType === 'WAREHOUSE_IDENTIFIER';

                // If this is an identifier and we haven't set one yet, use it
                if (isIdentifier && !identifierSet) {
                  // Don't replace the current identification image, just mark that one exists
                  identifierSet = true;
                  return;
                }

                // Add to additional images
                if (!isIdentifier || identifierSet) {
                  existingImages.push({
                    file: null as any, // No file object for existing attachments
                    url: attachment.files.ATTACHMENTS,
                    isIdentifier: false,
                    attachmentId: attachment.id,
                    fileName: attachment.fileName,
                  });
                }
              });

              // Set existing images (don't override, append to existing additional images)
              setAdditionalImages((prev) => [...existingImages, ...prev]);
            }

            message.success({ content: 'Item identified successfully', key: 'identify' });
          }
        } else {
          setUploadError({
            title: "Couldn't parse the image",
            message: 'Please retake image to proceed.',
            type: 'parse_error',
          });
          message.error({ content: 'Failed to identify item', key: 'identify' });
        }
      } catch {
        setUploadError({
          title: "Couldn't parse the image",
          message: 'Please retake image to proceed.',
          type: 'parse_error',
        });
        message.error({ content: 'Failed to identify item', key: 'identify' });
      } finally {
        setIsIdentifying(false);
      }
    },
    [id, order, identifyItem, message]
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      // Reset input value to allow selecting the same file again
      event.target.value = '';

      // Validate file
      const isImage = file.type.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|heic)$/i.test(file.name);
      const isLt5M = file.size / 1024 / 1024 < 5;

      if (!isImage) {
        message.error(`${file.name} is not an image file`);
        return;
      }
      if (!isLt5M) {
        message.error(`${file.name} must be smaller than 5MB`);
        return;
      }

      // Mark that user has started the flow
      if (!hasStarted) {
        setHasStarted(true);
      }

      // If no identification yet AND not currently identifying, this is the identifier image
      if (!identificationImage && !isIdentifying) {
        message.loading({ content: 'Identifying item...', key: 'identify' });
        await handleIdentification(file);
      } else {
        // This is an additional image (or user is taking photos during identification)
        const imageUrl = URL.createObjectURL(file);
        setAdditionalImages((prev) => [...prev, { file, url: imageUrl, isIdentifier: false }]);
        message.success('Photo added');
      }
    },
    [identificationImage, isIdentifying, handleIdentification, message, hasStarted]
  );

  const handleRetake = useCallback(() => {
    // Clear error and identifier, but KEEP additional images
    setUploadError(null);
    setIdentifiedItem(null);
    setIdentificationImage(null);
    // Don't clear additionalImages - keep them for the next identification

    // Open camera again (manually, not via auto-open effect)
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  }, []);

  const handleRemoveImage = useCallback(
    async (index: number) => {
      const imageToRemove = additionalImages[index];

      // If this is a backend attachment, delete it immediately
      if (imageToRemove.attachmentId) {
        try {
          message.loading({ content: 'Deleting image...', key: `delete-${imageToRemove.attachmentId}` });

          const { data } = await deleteAttachment({
            entityName: 'PURCHASE_ORDER_ITEM',
            id: imageToRemove.attachmentId,
            service: 'wrd',
          });

          if (!data?.success) {
            throw new Error('Failed to delete attachment');
          }

          if (!data.data?.inherited && imageToRemove.fileName) {
            await deleteAttachmentFile({
              id: imageToRemove.attachmentId,
              name: imageToRemove.fileName,
            });
          }

          message.success({ content: 'Image deleted successfully', key: `delete-${imageToRemove.attachmentId}` });
        } catch {
          message.error({
            content: 'Failed to delete image',
            key: `delete-${imageToRemove.attachmentId}`,
          });
          return; // Don't remove from UI if backend deletion failed
        }
      }

      // Remove from local state
      setAdditionalImages((prev) => prev.filter((_, i) => i !== index));
    },
    [additionalImages, deleteAttachment, deleteAttachmentFile, message]
  );

  const handleBack = useCallback(() => {
    navigate(`/warehouse/inbound/${id}`);
  }, [id, navigate]);

  const handleNext = useCallback(async () => {
    if (!identifiedItem || !identificationImage) return;

    try {
      setIsSaving(true);
      message.loading({ content: 'Saving images...', key: 'save-all' });

      // DEDUPLICATION LOGIC: Only upload NEW local files, skip existing backend attachments
      // EXCLUDE identifier image from upload (already used for identification)
      // Filter criteria:
      // 1. !img.attachmentId → exclude images already uploaded to backend (have attachment ID)
      // 2. img.file → only include images with File object (local captures)
      // 3. Only from additionalImages → exclude the identifier image
      // This ensures NO DUPLICATES and NO identifier image upload
      const warehouseFiles = additionalImages.filter((img) => !img.attachmentId && img.file);

      // If no new images to upload, just navigate (all images already uploaded)
      if (warehouseFiles.length === 0) {
        message.success({ content: 'Proceeding to next step', key: 'save-all' });

        // Update item step to STEP_3
        if (identifiedItem?.id) {
          try {
            await updateItemStep({ itemId: identifiedItem.id, step: 'STEP_3' });
          } catch (error) {
            console.error('Failed to update item step:', error);
            // Don't block navigation if step update fails
          }
        }

        navigate(`/warehouse/inbound/${id}/details`, {
          state: { itemId: identifiedItem.id, sku: identifiedItem.sku },
        });
        return;
      }

      const allAttachments = [];

      // Add warehouse attachments (identifier image is excluded)
      for (let i = 0; i < warehouseFiles.length; i++) {
        allAttachments.push({
          entityId: identifiedItem.id,
          entityName: 'PURCHASE_ORDER_ITEM',
          attachmentType: 'WAREHOUSE',
        });
      }

      // Step 1: Create attachment records
      const attachmentResponse: any = await createBulkAttachments({
        attachments: allAttachments,
        service: 'wrd',
      });

      if (!attachmentResponse?.data?.success) {
        throw new Error('Failed to create attachment records');
      }

      const attachmentIds = attachmentResponse.data.data;
      if (!Array.isArray(attachmentIds) || attachmentIds.length === 0) {
        throw new Error('No attachment IDs returned');
      }

      // Step 2: Upload all files (only warehouse files, identifier excluded)
      const formData = new FormData();
      formData.append('attachmentIds', attachmentIds.join(','));

      // Add warehouse files only
      warehouseFiles.forEach((img) => {
        formData.append('files', img.file);
      });
      formData.append('serviceName', 'WRD');

      const fileResponse: any = await uploadBulkAttachmentFiles({
        data: formData,
        entityName: 'PURCHASE_ORDER_ITEM',
      });

      if (!fileResponse?.data?.success) {
        throw new Error('Failed to upload files');
      }

      message.success({ content: 'All images saved successfully', key: 'save-all' });

      // Update item step to STEP_3
      if (identifiedItem?.id) {
        try {
          await updateItemStep({ itemId: identifiedItem.id, step: 'STEP_3' });
        } catch (error) {
          console.error('Failed to update item step:', error);
          // Don't block navigation if step update fails
        }
      }

      // Navigate directly to item details (skip verify screen)
      navigate(`/warehouse/inbound/${id}/details`, {
        state: { itemId: identifiedItem.id, sku: identifiedItem.sku },
      });
    } catch (err: any) {
      message.error({ content: err?.message || 'Failed to save images', key: 'save-all' });
    } finally {
      setIsSaving(false);
    }
  }, [
    identifiedItem,
    identificationImage,
    additionalImages,
    id,
    navigate,
    createBulkAttachments,
    uploadBulkAttachmentFiles,
    updateItemStep,
    message,
  ]);

  const handleWriteCode = useCallback(
    async (sku: string) => {
      if (!id || !sku.trim()) {
        return { success: false, error: 'SKU is required' };
      }

      try {
        const response: any = await searchItem({
          orderId: id,
          text: sku.trim(),
        });

        // Check for error response from RTK Query
        if (response.error) {
          const apiError = response.error;
          if (apiError.data?.data?.error_message && Array.isArray(apiError.data.data.error_message)) {
            // Network/API error - show in modal
            return { success: false, error: apiError.data.data.error_message[0] };
          }
          return { success: false, error: 'Failed to search item' };
        }

        if (!response?.data?.success) {
          return { success: false, error: 'Failed to search item' };
        }

        const itemData = response.data.data;
        const identificationType = itemData?.identificationType;

        // Always close modal and show respective status on capture screen
        if (identificationType === 'IDENTIFIED') {
          // Item found and matched in order
          setIdentifiedItem({
            id: itemData.purchaseOrderItemId || itemData.itemId,
            sku: itemData.sku,
            name: itemData.itemDescription,
            matched: true,
          });
          setUploadError(null);
          message.success('Item identified successfully');
        } else if (identificationType === 'NOT_IN_ORDER') {
          // Item exists but not in this order
          setIdentifiedItem(null);
          setUploadError({
            type: 'not_in_order',
            title: 'Identified item is not in the order',
            message: '',
            sku: itemData.sku,
          });
        } else if (identificationType === 'NOT_IDENTIFIED') {
          // Item not found - show parse error state
          setIdentifiedItem(null);
          setUploadError({
            type: 'parse_error',
            title: "Couldn't find the item",
            message: 'Please check the SKU and try again.',
          });
        } else {
          // Unexpected response
          setIdentifiedItem(null);
          setUploadError({
            type: 'parse_error',
            title: "Couldn't find the item",
            message: 'Unexpected response from server.',
          });
        }

        // Always close modal - status will be shown on capture screen
        return { success: true };
      } catch (error: any) {
        // Network error - show in modal
        const errorMessage = error?.message || 'Failed to search item';
        return { success: false, error: errorMessage };
      }
    },
    [id, searchItem, message]
  );

  const handleRaiseClaim = useCallback(() => {
    navigate(WAREHOUSE_ROUTES.raiseClaim.replace(':id', id || ''), {
      state: {
        itemSku: uploadError?.sku,
        fromStep: 'capture',
      },
    });
  }, [id, navigate, uploadError]);

  const completedCount =
    order?.itemsResponseList?.filter((item: any) => item.status === 'CHECKED' || item.status === 'CLAIMED').length || 0;

  const totalCount = order?.itemsResponseList?.length || 0;

  return {
    isLoading,
    hasStarted,
    isIdentifying: isIdentifying || isSaving,
    identifiedItem,
    uploadError,
    identificationImage,
    additionalImages,
    handleFileSelect,
    handleRetake,
    handleRemoveImage,
    handleNext,
    handleBack,
    handleRaiseClaim,
    handleWriteCode,
    completedCount,
    totalCount,
    fileInputRef,
  };
};
